import { createContext, useContext, useState, type ReactElement } from 'react';
import { Icon } from 'light-component';
import { Icons } from 'zf-web-components/icons';
import { Button } from '../../base';
import { Block, t } from '..';
import styles from './index.module.less';
import { useDrag } from './hooks/useDrag';
import { useHeight } from './hooks/useHeight';
import type { DocFrameProps, DocContextValue, TriggerProps, DocContextProps } from './types';

const DocContext = createContext<DocContextValue | null>(null);

export const DocProvider: React.FC<DocContextProps> = props => {
  const [visible, setVisible] = useState<boolean>(false);
  const [hash, setHash] = useState<string | undefined>();

  const showDoc = (hash?: string): void => {
    setVisible(true);
    setHash(hash);
  };

  return (
    <DocContext.Provider value={{ showDoc }}>
      {props.children}
      <DocFrame
        visible={visible}
        host={props.host}
        hash={hash}
        title={props.title ?? t('前往在线文档查看')}
        onClose={() => setVisible(false)}
      />
    </DocContext.Provider>
  );
};

// 自定义 Hook
export const useDoc = (): DocContextValue['showDoc'] => {
  const context = useContext(DocContext);
  if (context == null) {
    throw new Error('useDoc must be used within DocProvider');
  }

  return context.showDoc;
};

function DocFrame(props: DocFrameProps): ReactElement {
  const { host, hash, title } = props;
  const { height, ref } = useHeight(props.visible);
  const { position, handleMouseDown } = useDrag(ref);

  return (
    <Block if={props.visible}>
      <div
        className={styles.container}
        style={{ left: position.x, top: position.y }}
        ref={ref}
      >
        <section className={styles.header} onMouseDown={handleMouseDown}>
          <div className={styles.header}>
            <Icon name='DragIndicator' style={{ color: '#ccc' }} />
            <Button
              type='link'
              target='_blank'
              rel='noreferrer'
              style={{ color: '#e0392e' }}
              href={`${host}#${hash}`}
            >
              {title}
            </Button>
          </div>
          <Icons name='CloseOutlined' className={styles.close} onClick={props.onClose} />
        </section>
        <div className={styles.main}>
          <iframe style={{ width: '100%', height }} src={`${host}?anchor=hide#${hash}`}></iframe>
        </div>
        <section className={styles.footer}>
          <Button size='small' type='default' onClick={props.onClose}>{t('关闭')}</Button>
        </section>
      </div>
    </Block>
  );
}

export function DocTrigger(props: TriggerProps): ReactElement {
  const { hash } = props;
  const showDoc = useDoc();

  return (
    <Icons
      name='InfoCircleOutlined'
      style={{ color: '#5b8dff', marginLeft: 6 }}
      onClick={() => showDoc(hash)}
    />
  );
}
